import {
    getLineLayerConfig,
    getInterpolatedSelectableLineLayerWidth,
    LINE_LAYER_SIZE,
} from "@l2r-front/l2r-map"
import { getColorWithOpacity } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../common/constants/i18n"

import { impacts } from "./impacts"

export const ROADWORK_LAYER_WIDTH = LINE_LAYER_SIZE + 2

export const getRoadworkLegendConfig = (t, theme) => {
    return Object.values(impacts).map(legendValue => {
        return {
            color: theme.palette[legendValue.backgroundColor]?.main,
            label: t(I18N_NAMESPACE, legendValue.text),
        }
    })
}

const getRoadworksBaseConfig = (referentialColor, selectedRoad = null, selectedRoadwork = null, theme, alwaysFadeColors = false) => {
    const legendStyles = Object.entries(impacts).map(([impactValue, impactConfig]) => {
        const color = theme.palette[impactConfig.backgroundColor].main
        return [
            ["==", ["get", "impact", ["get", "technique", ["properties"]]], parseInt(impactValue)],
            alwaysFadeColors ? getColorWithOpacity(color, 0.5) : selectRoadworkLayerColorOpacity(color, 0.5, selectedRoad, selectedRoadwork),
        ]
    })

    const impactsValuesArray = Object.values(impacts)
    const lastColor = theme.palette[impactsValuesArray[impactsValuesArray.length - 1].backgroundColor].main
    legendStyles.push(lastColor)

    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["==", ["get", "technique", ["properties"]], null],
                theme.palette["_components/app-bar/button/default"].light,
                ["!=", ["typeof", ["get", "impact", ["get", "technique", ["properties"]]]], "number"],
                selectRoadworkLayerColorOpacity(referentialColor, referentialColor === "#FFFFFF" ? 0.85 : 0.5, selectedRoad, selectedRoadwork),
            ].concat(legendStyles.flat()),
            "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad),
            "line-dasharray": [
                "case",
                ["==", ["get", "technique", ["properties"]], null],
                ["literal", [1]],
                ["==", ["get", "impact", ["get", "technique", ["properties"]]], 10],
                ["literal", [1, 1]],
                ["literal", [1, 0]],
            ],
        },
        "layout": {
            "line-sort-key": ["get", "zIndex"],
        },
    })
}

export const getRoadworkTechniquesLayerStyle = (referentialColor, selectedTechnique = null, selectedRoad = null, filters = {}, theme, alwaysFadeColors = false, roadworkDetailUuid = null) => {
    const roadworksBaseConfig = getRoadworksBaseConfig(referentialColor, selectedRoad, null, theme, alwaysFadeColors)

    const filteredYears = filters.years
    const filteredTechniques = filters.techniques
    const filteredStatuses = filters.statuses
    const filteredTags = filters.tags
    const filteredAntiTags = filters.antiTags

    const [selectedTechniqueCode, selectedTechniqueYear, selectedTechniqueStatus] = selectedTechnique?.split("-") ?? [null]
    const yearFilter = selectedTechniqueYear ? [parseInt(selectedTechniqueYear)] : filteredYears
    const statusFilter = selectedTechniqueStatus ? selectedTechniqueStatus.split(",").map(status => parseInt(status)) : filteredStatuses
    const techniqueFilter = selectedTechniqueCode ? [selectedTechniqueCode] : filteredTechniques

    return {
        ...roadworksBaseConfig,
        "filter": [
            "all",
            !selectedRoad && ["!=", ["get", "technique", ["properties"]], null],
            yearFilter?.length && ["in", ["get", "year"], ["literal", yearFilter]],
            statusFilter?.length && ["in", ["get", "status"], ["literal", statusFilter]],
            techniqueFilter?.length && ["in", ["get", "code", ["get", "technique", ["properties"]]], ["literal", techniqueFilter]],
            roadworkDetailUuid && ["!=", ["get", "uuid", ["properties"]], roadworkDetailUuid],
            filteredTags?.length > 0
                ? ["all", ...filteredTags.map(tag => ["in", tag, ["get", "tags"]])]
                : true,
            filteredAntiTags?.length > 0
                ? ["all", ...filteredAntiTags.map(tag => ["!", ["in", tag, ["get", "tags"]]])]
                : true]
            .filter(Boolean),
        "line-sort-key": [
            "case",
            statusFilter?.length && ["in", ["get", "status"], ["literal", statusFilter]],
            2,
            selectedRoad ? ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad] : false,
            1,
            0,
        ],
    }
}

export const getRoadworksLayerStyle = (referentialColor, selectedRoadwork, selectedRoad = null, filters = {}, theme, roadworkDetailUuid = null) => {
    const roadworksBaseConfig = getRoadworksBaseConfig(referentialColor, selectedRoad, roadworkDetailUuid ?? selectedRoadwork, theme, false)
    const filteredYears = filters.years
    const filteredTechniques = filters.techniques
    const filteredStatuses = filters.statuses
    const filteredTags = filters.tags
    const filteredAntiTags = filters.antiTags

    return {
        ...roadworksBaseConfig,
        "filter": [
            "all",
            ["!=", ["get", "technique", ["properties"]], null],
            filteredYears?.length && ["in", ["get", "year"], ["literal", filteredYears]],
            filteredTechniques?.length && ["in", ["get", "code", ["get", "technique", ["properties"]]], ["literal", filteredTechniques]],
            filteredStatuses?.length && ["in", ["get", "status"], ["literal", filteredStatuses]],
            roadworkDetailUuid && ["!=", ["get", "uuid", ["properties"]], roadworkDetailUuid],
            filteredTags?.length > 0
                ? ["all", ...filteredTags.map(tag => ["in", tag, ["get", "tags"]])]
                : true,
            filteredAntiTags?.length > 0
                ? ["all", ...filteredAntiTags.map(tag => ["!", ["in", tag, ["get", "tags"]]])]
                : true,
        ]
            .filter(Boolean),

        layout: {
            ...roadworksBaseConfig.layout,
            "line-sort-key": [
                "case",
                selectedRoadwork ? ["==", ["get", "uuid", ["properties"]], selectedRoadwork] : false,
                2,
                selectedRoad ? ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad] : false,
                1,
                0,
            ],
        },
    }
}

export const selectRoadworkLayerColorOpacity = (color, opacity, selectedRoad = null, selectedRoadwork = null, selectedRoadColor = null) => {
    const selectedColor = selectedRoadColor ?? color

    return ["case",
        selectedRoadwork ? ["==", ["get", "uuid", ["properties"]], selectedRoadwork] : selectedRoad ? ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad] : true,
        selectedColor,
        getColorWithOpacity(color, opacity),
    ]
}
